import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { BookOpen, User, TrendingUp, Clock, FileText, Star, CheckCircle, AlertCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PROGRESS_STATUS = {
  sv: { beginner: 'Nybörjare', improving: 'Förbättras', good: 'Bra', excellent: 'Utmärkt' },
  en: { beginner: 'Beginner', improving: 'Improving', good: 'Good', excellent: 'Excellent' }
};

const PROGRESS_COLORS = {
  beginner: { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/30', pct: 25 },
  improving: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', border: 'border-yellow-500/30', pct: 50 },
  good: { bg: 'bg-green-500/15', text: 'text-green-400', border: 'border-green-500/30', pct: 75 },
  excellent: { bg: 'bg-purple-500/15', text: 'text-purple-400', border: 'border-purple-500/30', pct: 100 },
};

const LEVEL_LABELS = {
  sv: { beginner: 'Nybörjare — Quran Online Method', intermediate: 'Medel — Tajweed', advanced: 'Avancerad — Hifdh' },
  en: { beginner: 'Beginner — Quran Online Method', intermediate: 'Intermediate — Tajweed', advanced: 'Advanced — Hifdh' }
};

const ATT_ICONS = { present: CheckCircle, late: AlertCircle, absent: XCircle };
const ATT_COLORS_MAP = { present: 'text-green-400', late: 'text-yellow-400', absent: 'text-red-400' };

const StudentPortalPage = () => {
  const { token } = useParams();
  const { language } = useLanguage();
  const sv = language === 'sv';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    fetch(`${API_URL}/api/portal/${token}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(sv ? 'Elevprofil hittades inte.' : 'Student profile not found.'); setLoading(false); });
  }, [token, sv]);

  if (loading) return <div className="min-h-screen pt-20 flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-muted)' }}>Laddar...</div>;
  if (error) return (
    <div className="min-h-screen pt-20 flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="pricing-card text-center max-w-md mx-4"><p className="text-red-400">{error}</p></div>
    </div>
  );

  const { student, progress, lessons, attendance_stats } = data;
  const ps = student.progress_status || 'beginner';
  const pColors = PROGRESS_COLORS[ps] || PROGRESS_COLORS.beginner;

  const sections = [
    { id: 'overview', label: sv ? 'Översikt' : 'Overview', icon: User },
    { id: 'progress', label: sv ? 'Framsteg' : 'Progress', icon: TrendingUp },
    { id: 'lessons', label: sv ? 'Lektioner' : 'Lessons', icon: Clock },
    { id: 'homework', label: sv ? 'Läxor' : 'Homework', icon: FileText },
  ];

  const latestLesson = lessons?.[0];

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="student-portal">
      {/* Header */}
      <section className="py-10 islamic-pattern hero-gradient">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'Välkommen' : 'Welcome'}, {student.name}!
          </h1>
          <div className="flex items-center justify-center gap-3 flex-wrap mt-4">
            {student.level && (
              <span className="px-3 py-1 rounded-full text-sm bg-gold/10 text-gold border border-gold/30">
                {LEVEL_LABELS[language]?.[student.level] || student.level}
              </span>
            )}
            <span className={`px-3 py-1 rounded-full text-sm ${pColors.bg} ${pColors.text} border ${pColors.border}`}>
              {PROGRESS_STATUS[language]?.[ps] || ps}
            </span>
          </div>
        </div>
      </section>
      <div className="gold-divider"></div>

      {/* Navigation */}
      <div style={{ backgroundColor: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
        <div className="max-w-4xl mx-auto px-4 flex gap-1 overflow-x-auto py-2">
          {sections.map(s => {
            const Icon = s.icon;
            const active = activeSection === s.id;
            return (
              <button key={s.id} onClick={() => setActiveSection(s.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${active ? 'bg-gold text-black' : 'hover:bg-gold/10'}`}
                style={!active ? { color: 'var(--text-secondary)' } : {}}
                data-testid={`portal-tab-${s.id}`}>
                <Icon size={16} /> {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Overview */}
        {activeSection === 'overview' && (
          <div className="space-y-5" data-testid="portal-overview">
            {/* Stats row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: sv ? 'Kurs' : 'Course', value: student.course || '-' },
                { label: sv ? 'Lärare' : 'Teacher', value: student.teacher || '-' },
                { label: sv ? 'Totalt lektioner' : 'Total Lessons', value: attendance_stats?.total || 0 },
                { label: sv ? 'Närvaro' : 'Attendance', value: attendance_stats ? `${attendance_stats.rate}%` : '-' },
              ].map((item, i) => (
                <div key={i} className="pricing-card !p-4">
                  <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{item.label}</p>
                  <p className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>{item.value}</p>
                </div>
              ))}
            </div>

            {/* Attendance bar */}
            {attendance_stats && attendance_stats.total > 0 && (
              <div className="pricing-card !p-5">
                <p className="text-sm font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <CheckCircle size={16} className="text-gold" /> {sv ? 'Närvaroöversikt' : 'Attendance Overview'}
                </p>
                <div className="flex rounded-full h-3 overflow-hidden mb-3" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                  {attendance_stats.present > 0 && <div className="bg-green-500 transition-all" style={{ width: `${(attendance_stats.present / attendance_stats.total) * 100}%` }}></div>}
                  {attendance_stats.late > 0 && <div className="bg-yellow-500 transition-all" style={{ width: `${(attendance_stats.late / attendance_stats.total) * 100}%` }}></div>}
                  {attendance_stats.absent > 0 && <div className="bg-red-500 transition-all" style={{ width: `${(attendance_stats.absent / attendance_stats.total) * 100}%` }}></div>}
                </div>
                <div className="flex gap-4 text-xs">
                  <span className="flex items-center gap-1 text-green-400"><span className="w-2 h-2 rounded-full bg-green-500"></span> {sv ? 'Närvarande' : 'Present'}: {attendance_stats.present}</span>
                  <span className="flex items-center gap-1 text-yellow-400"><span className="w-2 h-2 rounded-full bg-yellow-500"></span> {sv ? 'Sen' : 'Late'}: {attendance_stats.late}</span>
                  <span className="flex items-center gap-1 text-red-400"><span className="w-2 h-2 rounded-full bg-red-500"></span> {sv ? 'Frånvarande' : 'Absent'}: {attendance_stats.absent}</span>
                </div>
              </div>
            )}

            {/* Progress bar */}
            <div className="pricing-card !p-5">
              <p className="text-sm font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <TrendingUp size={16} className="text-gold" /> {sv ? 'Framstegsnivå' : 'Progress Level'}
              </p>
              <div className="flex rounded-full h-2 overflow-hidden mb-2" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                <div className={`${pColors.bg.replace('/15', '')} transition-all`} style={{ width: `${pColors.pct}%`, backgroundColor: ps === 'beginner' ? '#3b82f6' : ps === 'improving' ? '#eab308' : ps === 'good' ? '#22c55e' : '#a855f7' }}></div>
              </div>
              <div className="flex justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
                <span>{sv ? 'Nybörjare' : 'Beginner'}</span>
                <span>{sv ? 'Förbättras' : 'Improving'}</span>
                <span>{sv ? 'Bra' : 'Good'}</span>
                <span>{sv ? 'Utmärkt' : 'Excellent'}</span>
              </div>
            </div>

            {/* Latest lesson */}
            {latestLesson && (
              <div className="pricing-card !p-5" style={{ borderColor: 'rgba(206, 150, 8, 0.3)' }}>
                <p className="text-sm font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <Clock size={16} className="text-gold" /> {sv ? 'Senaste lektionen' : 'Latest Lesson'} — {latestLesson.lesson_date}
                </p>
                <div className="space-y-1.5">
                  {latestLesson.what_was_read && <p className="text-sm" style={{ color: 'var(--text-secondary)' }}><span className="text-gold">{sv ? 'Lästes: ' : 'Read: '}</span>{latestLesson.what_was_read}</p>}
                  {latestLesson.tajweed_corrections && <p className="text-sm" style={{ color: 'var(--text-secondary)' }}><span className="text-gold">Tajweed: </span>{latestLesson.tajweed_corrections}</p>}
                  {latestLesson.homework_assigned && <p className="text-sm" style={{ color: 'var(--text-secondary)' }}><span className="text-gold">{sv ? 'Läxa: ' : 'Homework: '}</span>{latestLesson.homework_assigned}</p>}
                </div>
              </div>
            )}

            {/* Teacher feedback */}
            {progress?.teacher_feedback && (
              <div className="pricing-card !p-5">
                <p className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <Star size={16} className="text-gold" /> {sv ? 'Lärarens feedback' : "Teacher's Feedback"}
                </p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{progress.teacher_feedback}</p>
              </div>
            )}

            {/* Current homework highlight */}
            {progress?.homework && (
              <div className="pricing-card !p-5 border-l-4" style={{ borderLeftColor: 'var(--gold)' }}>
                <p className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <FileText size={16} className="text-gold" /> {sv ? 'Aktuell läxa' : 'Current Homework'}
                </p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{progress.homework}</p>
              </div>
            )}
          </div>
        )}

        {/* Progress */}
        {activeSection === 'progress' && (
          <div className="space-y-4" data-testid="portal-progress">
            <div className="pricing-card !p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <TrendingUp size={18} className="text-gold" /> {sv ? 'Studieframsteg' : 'Study Progress'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {progress?.current_book && (
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                    <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Nuvarande bok' : 'Current Book'}</p>
                    <p className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{progress.current_book}</p>
                  </div>
                )}
                {progress?.current_page > 0 && (
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                    <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Nuvarande sida' : 'Current Page'}</p>
                    <p className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{progress.current_page}</p>
                  </div>
                )}
                {progress?.current_surah && (
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                    <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Nuvarande surah' : 'Current Surah'}</p>
                    <p className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{progress.current_surah}</p>
                  </div>
                )}
              </div>
              {progress?.tajweed_notes && (
                <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                  <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Tajweed-anteckningar' : 'Tajweed Notes'}</p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{progress.tajweed_notes}</p>
                </div>
              )}
            </div>

            {/* Memorization section */}
            {(progress?.memorization_new || progress?.memorization_revision || (progress?.surahs_completed?.length > 0)) && (
              <div className="pricing-card !p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <BookOpen size={18} className="text-gold" /> {sv ? 'Memorering (Hifdh)' : 'Memorization (Hifdh)'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {progress.memorization_new && (
                    <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                      <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Ny memorering' : 'New Memorization'}</p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{progress.memorization_new}</p>
                    </div>
                  )}
                  {progress.memorization_revision && (
                    <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                      <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Revision' : 'Revision'}</p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{progress.memorization_revision}</p>
                    </div>
                  )}
                </div>
                {progress.surahs_completed?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>{sv ? 'Avslutade suror' : 'Completed Surahs'}</p>
                    <div className="flex flex-wrap gap-2">
                      {progress.surahs_completed.map((s, i) => (
                        <span key={i} className="px-2 py-1 rounded text-xs bg-green-500/15 text-green-400 border border-green-500/30">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!progress && (
              <div className="pricing-card !p-8 text-center">
                <p style={{ color: 'var(--text-muted)' }}>{sv ? 'Inga framstegsdata ännu.' : 'No progress data yet.'}</p>
              </div>
            )}
          </div>
        )}

        {/* Lessons with attendance */}
        {activeSection === 'lessons' && (
          <div className="space-y-3" data-testid="portal-lessons">
            <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <Clock size={18} className="text-gold" /> {sv ? 'Lektionshistorik' : 'Lesson History'}
            </h3>
            {lessons?.length > 0 ? lessons.map(l => {
              const att = l.attendance || 'present';
              const AttIcon = ATT_ICONS[att];
              return (
                <div key={l.id} className="pricing-card !p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{l.lesson_date}</span>
                      <AttIcon size={14} className={ATT_COLORS_MAP[att]} />
                      <span className={`text-xs ${ATT_COLORS_MAP[att]}`}>
                        {att === 'present' ? (sv ? 'Närvarande' : 'Present') : att === 'late' ? (sv ? 'Sen' : 'Late') : (sv ? 'Frånvarande' : 'Absent')}
                      </span>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-muted)' }}>{l.duration} min</span>
                  </div>
                  {l.what_was_read && <p className="text-sm" style={{ color: 'var(--text-secondary)' }}><span className="text-gold">{sv ? 'Lästes: ' : 'Read: '}</span>{l.what_was_read}</p>}
                  {l.tajweed_corrections && <p className="text-sm" style={{ color: 'var(--text-secondary)' }}><span className="text-gold">Tajweed: </span>{l.tajweed_corrections}</p>}
                  {l.homework_assigned && <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}><span className="text-gold">{sv ? 'Läxa: ' : 'Homework: '}</span>{l.homework_assigned}</p>}
                  {l.notes && <p className="text-xs mt-1 italic" style={{ color: 'var(--text-muted)' }}>{l.notes}</p>}
                </div>
              );
            }) : (
              <div className="pricing-card !p-8 text-center">
                <p style={{ color: 'var(--text-muted)' }}>{sv ? 'Inga lektioner registrerade ännu.' : 'No lessons recorded yet.'}</p>
              </div>
            )}
          </div>
        )}

        {/* Homework */}
        {activeSection === 'homework' && (
          <div className="space-y-4" data-testid="portal-homework">
            <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <FileText size={18} className="text-gold" /> {sv ? 'Läxor' : 'Homework'}
            </h3>
            {progress?.homework ? (
              <div className="pricing-card !p-6 border-l-4" style={{ borderLeftColor: 'var(--gold)' }}>
                <p className="text-sm font-medium mb-2 text-gold">{sv ? 'Aktuell läxa' : 'Current Homework'}</p>
                <p style={{ color: 'var(--text-secondary)' }}>{progress.homework}</p>
              </div>
            ) : null}
            {lessons?.filter(l => l.homework_assigned).length > 0 && (
              <div>
                <p className="text-sm font-medium mb-3" style={{ color: 'var(--text-primary)' }}>{sv ? 'Tidigare läxor' : 'Previous Homework'}</p>
                {lessons.filter(l => l.homework_assigned).map(l => (
                  <div key={l.id} className="pricing-card !p-4 mb-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{l.homework_assigned}</p>
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{l.lesson_date}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!progress?.homework && !lessons?.some(l => l.homework_assigned) && (
              <div className="pricing-card !p-8 text-center">
                <p style={{ color: 'var(--text-muted)' }}>{sv ? 'Inga läxor ännu.' : 'No homework yet.'}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentPortalPage;
